import React, { useState, useEffect, useMemo } from 'react';
import { claveSI } from '../../hooks/useProrrateo';
import { formatCurrency } from '../../utils/formatters';
import { generateProrrateoPDF } from '../../utils/exporters';

/**
 * Celda editable de la matriz de prorrateo.
 * - Mantiene su valor en estado local; persiste al salir (onBlur).
 * - Navegación tipo Excel con las flechas del teclado y Enter.
 */
const MatrizCelda = ({ value, rowIndex, colIndex, onSave }) => {
  const [val, setVal] = useState(value ? String(value) : '');

  useEffect(() => {
    setVal(value ? String(value) : '');
  }, [value]);

  const commit = () => {
    const pct = parseFloat(val) || 0;
    if (pct !== value) onSave(pct);
  };

  const moverFoco = (dRow, dCol) => {
    const sel = `.proceso-matriz-input[data-row="${rowIndex + dRow}"][data-col="${colIndex + dCol}"]`;
    const el = document.querySelector(sel);
    if (el) {
      el.focus();
      el.select();
    }
  };

  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        moverFoco(-1, 0);
        break;
      case 'ArrowDown':
      case 'Enter':
        e.preventDefault();
        moverFoco(1, 0);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        moverFoco(0, -1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        moverFoco(0, 1);
        break;
      default:
        break;
    }
  };

  return (
    <input
      className="proceso-matriz-input"
      type="number"
      min="0"
      max="100"
      step="0.01"
      data-row={rowIndex}
      data-col={colIndex}
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={handleKeyDown}
    />
  );
};

/**
 * Matriz completa de prorrateo: filas = servicio·instancia (con su total),
 * columnas = clientes. Cada celda es el % del costo asignado a ese cliente.
 * Columnas congeladas: Servicio/Instancia/Total a la izquierda; Σ y Prorrateado
 * a la derecha (el monto prorrateado que se va acumulando con cada %).
 */
export const ProrrateoMatriz = ({
  clientes,
  agrupado,
  prorrateo,
  onSetCelda,
  selectedMonth,
  showNotification
}) => {
  const round2 = (n) => Math.round(n * 100) / 100;

  const handleExportPDF = () => {
    generateProrrateoPDF({
      clientes,
      agrupado,
      prorrateo,
      selectedMonth,
      onSuccess: (t, m) => showNotification?.('success', t, m),
      onError: (t, m) => showNotification?.('error', t, m)
    });
  };

  const clienteTotales = useMemo(() => {
    const tot = {};
    clientes.forEach((c) => {
      tot[c.id] = 0;
    });
    agrupado.forEach((g) => {
      const row = prorrateo[claveSI(g.service, g.instance)] || {};
      clientes.forEach((c) => {
        const pct = row[c.id] || 0;
        if (pct) tot[c.id] += g.total * (pct / 100);
      });
    });
    return tot;
  }, [clientes, agrupado, prorrateo]);

  const totalCostos = useMemo(
    () => agrupado.reduce((s, g) => s + g.total, 0),
    [agrupado]
  );

  const granTotalProrrateado = useMemo(
    () => Object.values(clienteTotales).reduce((s, v) => s + v, 0),
    [clienteTotales]
  );

  // Progreso de la distribución: cuántos servicios suman exactamente 100%
  const serviciosCompletos = useMemo(
    () =>
      agrupado.filter((g) => {
        const row = prorrateo[claveSI(g.service, g.instance)] || {};
        const suma =
          Math.round(Object.values(row).reduce((s, v) => s + v, 0) * 100) / 100;
        return suma === 100;
      }).length,
    [agrupado, prorrateo]
  );

  const pctCompletado = agrupado.length
    ? Math.round((serviciosCompletos / agrupado.length) * 100)
    : 0;

  if (agrupado.length === 0) {
    return (
      <section className="proceso-matriz-section">
        <header className="proceso-matriz-header">Prorrateo</header>
        <p className="proceso-hint">
          Importa un CSV en la barra superior para distribuir los costos entre los clientes.
        </p>
      </section>
    );
  }

  return (
    <section className="proceso-matriz-section">
      <header className="proceso-matriz-header proceso-matriz-header-prog">
        <div className="proceso-matriz-headtop">
          <div className="proceso-matriz-titulo">
            <span className="proceso-matriz-nombre">Prorrateo</span>
            <span className="proceso-matriz-tip">
              {agrupado.length} costos × {clientes.length} clientes · usa las flechas para navegar
            </span>
          </div>

          <button className="proceso-pdf-btn" onClick={handleExportPDF}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Exportar informe PDF
          </button>
        </div>

        <div className="proceso-matriz-progreso">
          <div className="matriz-progreso-top">
            <span className="matriz-progreso-label">Distribución completada</span>
            <span className={`matriz-progreso-valor ${pctCompletado === 100 ? 'completo' : ''}`}>
              {serviciosCompletos}/{agrupado.length} servicios · {pctCompletado}%
            </span>
          </div>
          <div className="matriz-progreso-bar">
            <div
              className={`matriz-progreso-fill ${pctCompletado === 100 ? 'completo' : ''}`}
              style={{ width: `${pctCompletado}%` }}
            />
          </div>
          <div className="matriz-progreso-pie">
            <span className="matriz-progreso-monto ok">
              Prorrateado {formatCurrency(granTotalProrrateado)}
            </span>
            <span className="matriz-progreso-monto falta">
              Falta {formatCurrency(Math.max(0, totalCostos - granTotalProrrateado))}
            </span>
          </div>
        </div>
      </header>

      {clientes.length === 0 ? (
        <p className="proceso-hint">Crea clientes en la columna 1 para repartir los costos.</p>
      ) : (
        <div className="proceso-matriz-wrap">
          <table className="proceso-matriz">
            <thead>
              <tr>
                <th className="mz-svc">Servicio</th>
                <th className="mz-inst">Instancia</th>
                <th className="mz-total">Total</th>
                {clientes.map((c) => (
                  <th key={c.id} className="mz-cli">{c.name}</th>
                ))}
                <th className="mz-suma">Σ %</th>
                <th className="mz-prorr">Prorrateado</th>
              </tr>
            </thead>
            <tbody>
              {agrupado.map((g, rowIndex) => {
                const k = claveSI(g.service, g.instance);
                const row = prorrateo[k] || {};
                const suma = round2(
                  Object.values(row).reduce((s, v) => s + v, 0)
                );
                const estado = suma === 100 ? 'ok' : suma > 100 ? 'bad' : '';
                const prorrateado = g.total * (suma / 100);
                return (
                  <tr key={k}>
                    <td className="mz-svc" title={g.service}>{g.service}</td>
                    <td className="mz-inst" title={g.instance}>{g.instance}</td>
                    <td className="mz-total">{formatCurrency(g.total)}</td>
                    {clientes.map((c, colIndex) => (
                      <td key={c.id} className="mz-cli">
                        <MatrizCelda
                          value={row[c.id] || 0}
                          rowIndex={rowIndex}
                          colIndex={colIndex}
                          onSave={(pct) => onSetCelda(g.service, g.instance, c.id, pct)}
                        />
                      </td>
                    ))}
                    <td className={`mz-suma ${estado}`}>{suma}%</td>
                    <td className={`mz-prorr ${estado}`}>{formatCurrency(prorrateado)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td className="mz-svc">Total por cliente</td>
                <td className="mz-inst" />
                <td className="mz-total">{formatCurrency(totalCostos)}</td>
                {clientes.map((c) => (
                  <td key={c.id} className="mz-cli mz-cli-total">
                    {formatCurrency(clienteTotales[c.id] || 0)}
                  </td>
                ))}
                <td className="mz-suma" />
                <td className="mz-prorr">{formatCurrency(granTotalProrrateado)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
};
